import { Checkbox, Form, Input, InputNumber, Spin, Typography } from 'antd';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
    administratorGetAction,
    administratorValueSetAction,
} from '~Administrators/actions';
import { administratorSelector } from '~Administrators/selectors';
import { authCurrentUserSelector } from '~Auth/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import IAuthUser from '~Auth/IAuthUser';
import RoleEnum from '~Api/Administrator/RoleEnum';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import RoleModal from './RoleModal';
import ModalStateEnum from '~UI/ModalStateEnum';
import { IDictionary } from '~utilities/IDictionary';

const roleNames: IDictionary<string> = {
    [RoleEnum.BusinessDevelopmentManager]: 'Business Development Manager',
    [RoleEnum.CreditManager]: 'Credit Manager',
    [RoleEnum.InternalBusinessDevelopmentManager]: 'Internal Business Development Manager',
    [RoleEnum.InvestorManager]: 'Investor Manager',
    [RoleEnum.LoanManager]: 'Loan Manager',
    [RoleEnum.SeniorBusinessDevelopmentManager]: 'Senior Business Development Manager',
    [RoleEnum.SeniorCreditManager]: 'Senior Credit Manager',
    [RoleEnum.SeniorInvestorManager]: 'Senior Investor Manager',
    [RoleEnum.SeniorLoanManager]: 'Senior Loan Manager',
    [RoleEnum.SuperAdmin]: 'Super Admin',
};

interface IMatch {
    administratorUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IState {
    roleModalState: ModalStateEnum;
}

interface IPropsSelector {
    administrator: IAdministrator;
    currentUser: IAuthUser;
}

interface IPropsDispatch {
    administratorGet: () => void;
    administratorValueSet: (key: keyof IAdministrator, value: boolean | number | string | string[]) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Edit extends React.Component<Props, IState> {
    public state: IState = {
        roleModalState: ModalStateEnum.Off,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeName = this.onChangeName.bind(this);
        this.onChangeEmail = this.onChangeEmail.bind(this);
        this.onChangeHubspotOwnerId = this.onChangeHubspotOwnerId.bind(this);
        this.onChangePhone = this.onChangePhone.bind(this);
        this.onChangePhotoUrl = this.onChangePhotoUrl.bind(this);
        this.onChangeTitle = this.onChangeTitle.bind(this);
        this.onChangeDealMaximumValue = this.onChangeDealMaximumValue.bind(this);
        this.onChangeDealMinimumValue = this.onChangeDealMinimumValue.bind(this);
        this.onChangeAutoAssign = this.onChangeAutoAssign.bind(this);
        this.onClickRoleModal = this.onClickRoleModal.bind(this);
        this.onCloseRoleModal = this.onCloseRoleModal.bind(this);

    }

    public componentDidMount(): void {
        const { administrator } = this.props;

        if (!administrator) {
            this.props.administratorGet();
        }
    }

    public render(): JSX.Element {
        const { administrator, currentUser, match } = this.props;
        const { roleModalState } = this.state;

        if (!administrator) {
            return (
                <Layout administratorUuid={match.params.administratorUuid} section='edit'>
                    <Typography.Title level={2}>Edit</Typography.Title>
                    <Spin />
                </Layout>
            );
        }

        const administratorRoleLabel: JSX.Element = currentUser.uuid !== administrator.uuid ? (
            <a onClick={this.onClickRoleModal}>{administrator.role ? roleNames[administrator.role] : 'None'}</a>
        ) : (
            <span>{roleNames[administrator.role]}</span>
        );

        const isBdm: boolean = [
            RoleEnum.BusinessDevelopmentManager,
            RoleEnum.InternalBusinessDevelopmentManager,
            RoleEnum.SeniorBusinessDevelopmentManager,
        ].includes(administrator.role);

        const dealMinimumBlock: JSX.Element = administrator.leadsAutoAssign && (
            <Form.Item label='Deal Minimum' className='deal-minimum-value' extra={currencyFormatter.format(administrator.dealMinimumValue)}>
                <InputNumber addonBefore='$' onChange={this.onChangeDealMinimumValue} value={administrator.dealMinimumValue} />
            </Form.Item>
        );

        const dealMaximumBlock: JSX.Element = administrator.leadsAutoAssign && (
            <Form.Item label='Deal Maximum' className='deal-maximum-value' extra={currencyFormatter.format(administrator.dealMaximumValue)}>
                <InputNumber addonBefore='$' onChange={this.onChangeDealMaximumValue} value={administrator.dealMaximumValue} />
            </Form.Item>
        );

        const hubspotOwnerIdBlock: JSX.Element = isBdm && (
            <Form.Item label='Hubspot Owner Id' className='hubspot-owner-id'>
                <Input onChange={this.onChangeHubspotOwnerId} value={administrator.hubspotOwnerId} />
            </Form.Item>
        );

        const roleModalBlock: JSX.Element = ModalStateEnum.Off !== roleModalState && (
            <RoleModal
                administrator={administrator}
                isOpen={roleModalState}
                onCancel={this.onCloseRoleModal}
            />
        );

        return (
            <Layout administratorUuid={match.params.administratorUuid} section='edit'>
                <Typography.Title level={2}>Edit</Typography.Title>
                <Form.Item label='Name' className='name'>
                    <Input onChange={this.onChangeName} value={administrator.name} />
                </Form.Item>
                <Form.Item label='Email' className='email'>
                    <Input onChange={this.onChangeEmail} value={administrator.email} />
                </Form.Item>
                <Form.Item label='Phone' className='phone'>
                    <Input onChange={this.onChangePhone} value={administrator.phone} />
                </Form.Item>
                <Form.Item label='Photo URL' className='photo-url'>
                    <Input onChange={this.onChangePhotoUrl} value={administrator.photoUrl} />
                </Form.Item>
                <Form.Item label='Title' className='title'>
                    <Input onChange={this.onChangeTitle} value={administrator.title} />
                </Form.Item>
                <Form.Item label='Role' className='role'>
                    {administratorRoleLabel}
                    {roleModalBlock}
                </Form.Item>
                {hubspotOwnerIdBlock}
                <Form.Item label='Auto Assign Leads'>
                    <Checkbox onChange={this.onChangeAutoAssign} checked={administrator.leadsAutoAssign} />
                </Form.Item>
                {dealMinimumBlock}
                {dealMaximumBlock}
            </Layout>
        );
    }

    private onChangeAutoAssign(event: CheckboxChangeEvent): void {
        this.props.administratorValueSet('leadsAutoAssign', event.target.checked);
    }

    private onChangeName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('name', event.target.value);
    }

    private onChangeEmail(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('email', event.target.value);
    }

    private onChangePhone(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('phone', event.target.value);
    }

    private onChangeHubspotOwnerId(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('hubspotOwnerId', event.target.value);
    }

    private onChangePhotoUrl(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('photoUrl', event.target.value);
    }

    private onChangeTitle(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.administratorValueSet('title', event.target.value);
    }

    private onChangeDealMaximumValue(value: number): void {
        this.props.administratorValueSet('dealMaximumValue', value);
    }

    private onChangeDealMinimumValue(value: number): void {
        this.props.administratorValueSet('dealMinimumValue', value);
    }

    private onClickRoleModal(): void {
        this.setState({
            roleModalState: ModalStateEnum.Open,
        });
    }

    private onCloseRoleModal(): void {
        this.setState({
            roleModalState: ModalStateEnum.Closed,
        });
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        administrator: administratorSelector(state, ownProps.match.params.administratorUuid),
        currentUser: authCurrentUserSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        administratorGet: () => dispatch(administratorGetAction(ownProps.match.params.administratorUuid)),
        administratorValueSet: (key: keyof IAdministrator, value: boolean | number | string | string[]) => dispatch(administratorValueSetAction(ownProps.match.params.administratorUuid, key, value)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Edit);
